import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getPagedUserCollection,
  togglePlayed,
  removeFromCollection,
  toggleOwned,
  toggleWishlist,
  updatePlayCount,
  removePlayedGameHistory,
  CollectionFilters,
  Pagination,
  UserGame
} from '../../../lib/game/collectionService';
import { rateGame } from '../../../lib/game/ratingService';
import useDebounce from '../../../hooks/useDebounce';

const PAGE_SIZE = 20;

// ATUALIZADO: Definir o tipo para sortBy para incluir todos os valores válidos da RPC
type SortByType = 'game_name' | 'ug_rating' | 'ug_updated_at' | 'g_average_rating' | 'g_complexity' | 'g_year' | 'ug_play_count'; 

// Manter a interface CollectionSort local, mas usando o SortByType atualizado.
// Idealmente, importaríamos de collectionService se não houvesse conflito.
interface CollectionSort {
  sortBy: SortByType; 
  ascending: boolean;
}

interface UsePlayedGamesCollectionProps {
  initialSearchText?: string;
  initialSortConfig?: CollectionSort; // Usar o tipo local
  initialPage?: number;
}

export const usePlayedGamesCollection = ({
  initialSearchText = '',
  // Ajustar o valor inicial para um SortByType válido, se necessário
  initialSortConfig = { sortBy: 'ug_updated_at', ascending: false }, // Exemplo: ordenando por mais recente
  initialPage = 1,
}: UsePlayedGamesCollectionProps = {}) => {
  const { user, isAdmin } = useAuth();
  const [pagedPlayedGames, setPagedPlayedGames] = useState<UserGame[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState<string | null>(null);

  const [searchText, setSearchText] = useState(initialSearchText);
  const [sortConfig, setSortConfig] = useState<CollectionSort>(initialSortConfig); // Usar o tipo local
  const [currentPage, setCurrentPage] = useState(initialPage);

  const debouncedSearchText = useDebounce(searchText, 500);

  const loadPagedPlayedGames = useCallback(async () => {
    if (!user?.id) {
      setPagedPlayedGames([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.log("[HOOK_PLAYED_GAMES] Carregando jogos jogados paginados...", { debouncedSearchText, sortConfig, currentPage });

    try {
      const filters: CollectionFilters = {
        type: 'played',
        searchText: debouncedSearchText || null,
      };
      const pagination: Pagination = {
        page: currentPage,
        pageSize: PAGE_SIZE,
      };

      // Remover toda a lógica de busca/fallback com getPlayedGamesAdaptive
      // Chamar getPagedUserCollection diretamente

      // O sortConfig já deve estar no formato correto (SortByType)
      const result = await getPagedUserCollection(user.id, filters, sortConfig, pagination);

      console.log("[HOOK_PLAYED_GAMES] Resultado recebido da RPC:", result);

      if (result && result.data) {
        setPagedPlayedGames(result.data);
        setTotalCount(result.count ?? 0); // Usar ?? 0 para garantir número
        if (isAdmin) {
          setDiagnosticInfo(`Jogos Jogados carregados via RPC. UserId: ${user.id}, Page: ${currentPage}, Filters: ${JSON.stringify(filters)}, Sort: ${JSON.stringify(sortConfig)}, Total: ${result.count}, Encontrados: ${result.data.length}`);
        }
      } else {
         setPagedPlayedGames([]);
         setTotalCount(0);
         if (isAdmin) {
           setDiagnosticInfo(`RPC retornou resultado vazio ou inválido para Jogos Jogados. UserId: ${user.id}, Filters: ${JSON.stringify(filters)}, Sort: ${JSON.stringify(sortConfig)}`);
         }
      }

    } catch (err) {
      console.error('[HOOK_PLAYED_GAMES] Erro ao carregar jogos jogados:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Não foi possível carregar seus jogos jogados: ${errorMessage}`);
      if (isAdmin) {
        setDiagnosticInfo(`Erro geral no hook: ${errorMessage}`);
      }
      setPagedPlayedGames([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, debouncedSearchText, sortConfig, currentPage, isAdmin]);

  useEffect(() => {
    loadPagedPlayedGames();
  }, [loadPagedPlayedGames]); // A dependência principal é a própria função memoizada

  // Handlers
  const handleSearchChange = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  // ATUALIZADO: Simplificar handleSortChange
  const handleSortChange = useCallback((newSortByValue: string) => {
    // Assumimos que o componente que chama isso (GamesCollectionView?) envia valores válidos
    const mappedSortBy = newSortByValue as SortByType; // Cast direto

    setSortConfig(prevSort => {
      const isAscending = prevSort.sortBy === mappedSortBy ? !prevSort.ascending : true;
      // Se estava ordenando pelo mesmo, inverte. Senão, default é ascendente (ou como definido)
      return { sortBy: mappedSortBy, ascending: isAscending }; 
    });
    setCurrentPage(1); // Resetar página ao mudar ordenação
  }, []); // Adicionar dependências vazias se não depender de nada externo

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Ações nos jogos (com recarregamento)
  const handleRateGame = async (gameId: string, rating: number) => {
    if (!user?.id) return;
    try {
      await rateGame(user.id, gameId, rating);
      loadPagedPlayedGames(); // Recarrega após a ação
    } catch (error) {
      console.error('Erro ao avaliar jogo:', error);
      setError('Não foi possível avaliar o jogo. Tente novamente.');
    }
  };

  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
     if (!user?.id) return;
     try {
       await togglePlayed(user.id, gameId, isPlayed);
       loadPagedPlayedGames(); // Recarrega
     } catch (error) {
       console.error('Erro ao marcar jogo como jogado:', error);
       setError('Não foi possível atualizar o status de jogado. Tente novamente.');
     }
   };

  const handleRemoveGame = async (gameId: string) => {
    if (!user?.id) return;
    // Confirmação pode ser movida para o componente que chama
    try {
      await removeFromCollection(user.id, gameId);
      loadPagedPlayedGames(); // Recarrega
    } catch (error) {
      console.error('Erro ao remover jogo:', error);
      setError('Não foi possível remover o jogo. Tente novamente.');
    }
  };

  const handleRemovePlayedGameHistory = async (gameId: string) => {
    if (!user?.id) return;
    try {
      await removePlayedGameHistory(user.id, gameId);
      loadPagedPlayedGames(); // Recarrega a lista após zerar o contador
    } catch (error) {
      console.error('Erro ao remover histórico de jogo:', error);
      setError('Não foi possível remover o histórico de jogadas. Tente novamente.');
    }
  };

  const handleUpdatePlayCount = async (gameId: string, playCount: number) => {
    if (!user?.id) return;
    try {
      await updatePlayCount(user.id, gameId, playCount);
      loadPagedPlayedGames(); // Recarrega
    } catch (error) {
      console.error('Erro ao atualizar contagem de jogadas:', error);
      setError('Não foi possível atualizar a contagem de jogadas. Tente novamente.');
    }
  };

  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user?.id) return;
    try {
      await toggleOwned(user.id, gameId, isOwned);
      loadPagedPlayedGames(); // Recarrega
    } catch (error) {
       console.error('Erro toggleOwned:', error); setError('Erro ao atualizar owned.');
     }
  };

  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user?.id) return;
    try {
      await toggleWishlist(user.id, gameId, isWishlist);
      loadPagedPlayedGames(); // Recarrega
    } catch (error) {
       console.error('Erro toggleWishlist:', error); setError('Erro ao atualizar wishlist.');
     }
  };


  return {
    pagedPlayedGames,
    totalCount,
    loading,
    error,
    diagnosticInfo,
    searchText,
    sortConfig,
    currentPage,
    totalPages: Math.ceil(totalCount / PAGE_SIZE),
    handleSearchChange,
    handleSortChange,
    handlePageChange,
    handleRateGame,
    handleTogglePlayed,
    handleRemoveGame,
    handleRemovePlayedGameHistory,
    handleUpdatePlayCount,
    handleToggleOwned,
    handleToggleWishlist,
    reloadGames: loadPagedPlayedGames, // Exporta a função de recarregar
  };
}; 